/**
 * Category Hooks
 * ==============
 * React Query hooks for category operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { categoryApi } from '@/services/api'
import type { CategoryCreate, CategoryUpdate } from '@/types'

// Query keys
export const categoryKeys = {
  all: ['categories'] as const,
  lists: () => [...categoryKeys.all, 'list'] as const,
  list: (includeChildren?: boolean) => [...categoryKeys.lists(), { includeChildren }] as const,
  details: () => [...categoryKeys.all, 'detail'] as const,
  detail: (id: number) => [...categoryKeys.details(), id] as const,
}

/**
 * Hook to fetch all categories.
 */
export function useCategories(includeChildren = false) {
  return useQuery({
    queryKey: categoryKeys.list(includeChildren),
    queryFn: () => categoryApi.list(includeChildren),
  })
}

/**
 * Hook to fetch a single category.
 */
export function useCategory(id: number) {
  return useQuery({
    queryKey: categoryKeys.detail(id),
    queryFn: () => categoryApi.get(id),
    enabled: !!id,
  })
}

/**
 * Hook to create a new category.
 */
export function useCreateCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CategoryCreate) => categoryApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() })
    },
  })
}

/**
 * Hook to update a category.
 */
export function useUpdateCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CategoryUpdate }) =>
      categoryApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: categoryKeys.detail(id) })
    },
  })
}

/**
 * Hook to delete a category.
 */
export function useDeleteCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reassignTo }: { id: number; reassignTo?: number }) =>
      categoryApi.delete(id, reassignTo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() })
    },
  })
}
